// frontend/src/pages/Tickets/EditTicketForm.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Form, Input, Select, Button, DatePicker, message, Spin, Typography, Row, Col, Card, Checkbox, Tooltip, Upload, Tabs, Modal, Table, Popconfirm, Space, Dropdown, Menu, Badge } from 'antd';
import {
  SendOutlined, UploadOutlined, CloseCircleOutlined, SaveOutlined, ClearOutlined, DeleteOutlined, EditOutlined, MessageOutlined, FileTextOutlined, DownloadOutlined, PlusOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

// Context and API imports
import api from '../../utils/api';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import { usePermissions, PERMISSION_LEVELS } from '../../hooks/usePermissions';

import TicketInvolvedAssetsManager from '../../components/TicketInvolvedAssetsManager/TicketInvolvedAssetsManager';
import PdfPreviewModal from '../../components/PdfPreviewModal';

// TinyMCE imports
import { Editor } from '@tinymce/tinymce-react';

// Date/Time libraries and plugins
import moment from 'moment-timezone'; 
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// CSS import
import './EditTicketForm.css';

// Extend dayjs with plugins globally as soon as they are imported.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

// Set default timezone for dayjs to avoid ambiguity.
dayjs.tz.setDefault('Asia/Kuala_Lumpur');

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

// Define status IDs for clarity (using confirmed values)
const STATUS_UNASSIGNED_ID = 9;
const STATUS_IN_PROGRESS_ID = 2;
const CLOSED_STATUS_ID = 6;
const STATUS_CANCELLED_ID = 7;

// Helper function to convert snake_case to camelCase
const convertSnakeToCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      newObj[camelKey] = obj[key];
    }
  }
  return newObj;
};

const convertCamelToSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      newObj[snakeKey] = obj[key];
    }
  }
  return newObj;
};

const EditTicketForm = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm(); // Main ticket form instance

  // Authentication and App Settings Contexts
  const { user } = useAuth();
  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();
  const permissions = usePermissions(user);

  // --- Loading States ---
  const [loading, setLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [ticketError, setTicketError] = useState(null);

  // --- Static Data States ---
  const [staticDataLoaded, setStaticDataLoaded] = useState(false);
  const [allTicketCategories, setAllTicketCategories] = useState([]);

  // --- Ticket Data States ---
  const [ticketData, setTicketData] = useState(null);
  const [descriptionHtml, setDescriptionHtml] = useState('<p></p>');
  const [isTicketClosed, setIsTicketClosed] = useState(false);
  const [ticketCRs, setTicketCRs] = useState([]); // CRs linked to this ticket (for Manage CR button)

  // --- PDF Preview/Download States ---
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [pdfPreviewBuffer, setPdfPreviewBuffer] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [isDateModalVisible, setIsDateModalVisible] = useState(false);
  const [dateForm] = Form.useForm();
  const [tempTicketData, setTempTicketData] = useState(null);
  const [datePickerKey, setDatePickerKey] = useState(0);

  // --- Dropdown/Reference Data States ---
  const [contracts, setContracts] = useState([]);
  const [contractProducts, setContractProducts] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [comments, setComments] = useState([]);
  const [contractSpecificCategories, setContractSpecificCategories] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [caseReportedDate, setCaseReportedDate] = useState(null);
  const [closedAt, setClosedAt] = useState(dayjs().tz('Asia/Kuala_Lumpur'));

  // --- Filtered Display States ---
  const [displayProduct, setDisplayProduct] = useState(null);
  const [displayedPriorities, setDisplayedPriorities] = useState([]);
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState(null);
  const [filteredProducts, setInternalFilteredProducts] = useState([]);

  // --- Attachment States ---
  const [fileList, setFileList] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  // --- Commenting/Reply States ---
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [newStatusIdForComment, setNewStatusIdForComment] = useState(null);
  const [originalStatusId, setOriginalStatusId] = useState(null);

  const [newGeneralCommentText, setNewGeneralCommentText] = useState('');
  const [isEditCommentModalVisible, setIsEditCommentModalVisible] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [deleteCommentModalVisible, setDeleteCommentModalVisible] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);

  // --- Rich Text Editor Reference ---
  const editorRef = useRef(null);

  // --- App Settings based formatting ---
  const dateFormat = appSettings?.dateFormat || 'YYYY-MM-DD';
  const timeFormat = appSettings?.timeFormat || 'HH:mm:ss';
  const displayDateFormat = `${dateFormat} ${timeFormat}`;

// Update the canUserEditTicket computation in EditTicketForm.js
const canUserEditTicket = useMemo(() => {
  if (!user || !ticketData) return false;
  
  // If ticket is closed (status ID: 6), only allow System Admin and Internal Supervisor to edit
  if (ticketData.currentStatusId === 6) { // Closed status
    const userRoleName = user.roleName || '';
    const isSystemAdmin = userRoleName === 'System Admin';
    const isInternalSupervisor = userRoleName === 'Internal Staff Supervisor';
    
    // Only System Admin and Internal Supervisor can edit closed tickets
    return isSystemAdmin || isInternalSupervisor;
  }
  
  // For non-closed tickets, use the normal permission check
  return permissions.canEditTicket(ticketData);
}, [user, ticketData, permissions]);

  const isAssigneeDisabled = useMemo(() => {
    return permissions.isAssigneeDisabled;
  }, [permissions]);

  const canGenerateReport = useMemo(() => {
    return permissions.canGenerateReport;
  }, [permissions]);

  const canEditAssets = useMemo(() => {
    return permissions.canEdit('tickets', PERMISSION_LEVELS.ASSIGNED);
  }, [permissions]);

  const isTicketCurrentlyUnassigned = ticketData?.currentStatusId === STATUS_UNASSIGNED_ID;

  // Check if user can edit contract field - Only System Admin and Internal Supervisor
  const canEditContract = useMemo(() => {
    if (!user) return false;
    return user.roleName === 'System Admin' || user.roleName === 'Internal Staff Supervisor';
  }, [user]);

  // Check if user can add assets - creator/assignee or System Admin/Internal Supervisor
  const canAddAssets = useMemo(() => {
    if (!user || !ticketData) return false;
    
    const isCreator = user.id === ticketData.createdByUserId;
    const isAssignee = user.id === ticketData.assignedToUserId;
    const isAdminOrSupervisor = user.roleName === 'System Admin' || user.roleName === 'Internal Staff Supervisor';
    
    return isCreator || isAssignee || isAdminOrSupervisor;
  }, [user, ticketData]);

  // Fetch CR list for this ticket (used by the Manage CR button)
  useEffect(() => {
    if (!ticketData?.id) {
      setTicketCRs([]);
      return;
    }
    let cancelled = false;
    api.get(`/api/time-tracking/cr/ticket/${ticketData.id}`)
      .then(res => {
        if (cancelled) return;
        if (res.data?.success) {
          setTicketCRs(res.data.data || []);
        } else {
          setTicketCRs([]);
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[EditTicketForm] Failed to fetch ticket CRs:', err);
        setTicketCRs([]);
      });
    return () => { cancelled = true; };
  }, [ticketData?.id]);

  // Notification helper functions
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Helper for extracting array data from API responses
  const extractArrayData = useCallback((response, expectedKey = null) => {
    if (!response || !response.data) {
      return [];
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (typeof response.data === 'object' && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    if (expectedKey && typeof response.data === 'object' && Array.isArray(response.data[expectedKey])) {
      return response.data[expectedKey];
    }
    return [];
  }, []);

  // Fetch product for display when not in user's assigned list
  const fetchProductForDisplay = useCallback(async (productId) => {
    try {
      const response = await api.get(`/api/products/${productId}`);
      if (response.data.success && response.data.data) {
        setDisplayProduct(response.data.data);
      }
    } catch (error) {
      console.error(`Error fetching product ${productId} for display:`, error);
    }
  }, []);

// Replace the existing handleContractChange function with this updated version
// Update the handleContractChange function to properly handle product field state
const handleContractChange = useCallback(async (selectedContractId) => {
  setSelectedContractId(selectedContractId);
  // Reset form fields and states that depend on the contract
  form.setFieldsValue({
    ticketCategoryId: undefined,
    productCategoryId: undefined,
    productId: undefined,
    priorityId: undefined,
    agreedResolutionPeriodValue: undefined
  });
  setContractSpecificCategories([]);
  setDisplayedPriorities(priorities);
  setContractProducts([]);
  setInternalFilteredProducts([]);
  setSelectedProductCategoryId(null); // Reset the selected product category ID

  if (selectedContractId) {
    const loadingKey = 'fetchContractData';
    const hideLoading = showLoading(loadingKey, 'Loading contract-specific data...');
    try {
      // Fetch contract-specific ticket categories and SLA definitions
      const contractSlasRes = await api.get(`/api/contracts/${selectedContractId}/sla-definitions`);
      const contractSlas = extractArrayData(contractSlasRes, 'slaDefinitions');

      const categoriesWithSlaIds = new Set(contractSlas.map(sla => String(sla.ticketCategoryId)));
      const categoriesWithSla = allTicketCategories.filter(cat => categoriesWithSlaIds.has(String(cat.id)));
      setContractSpecificCategories(categoriesWithSla);

      // Fetch products for the selected contract using the updated pattern
      const contractProductsRes = await api.get(`/api/contracts/${selectedContractId}/products`);
      const fetchedContractProducts = extractArrayData(contractProductsRes, 'data').map(product => ({
        ...product,
        productCategoryId: product.product_category_id || product.productCategoryId,
      }));
      setContractProducts(fetchedContractProducts);
      
      // After fetching, check if we need to preserve any existing product category selection
      // (This handles the case where we're editing an existing ticket)
      const currentProductCategoryId = form.getFieldValue('productCategoryId');
      if (currentProductCategoryId) {
          const newFilteredProducts = fetchedContractProducts.filter(product =>
            String(product.productCategoryId) === String(currentProductCategoryId)
          );
          setInternalFilteredProducts(newFilteredProducts);
          setSelectedProductCategoryId(currentProductCategoryId);
      } else {
          setInternalFilteredProducts([]);
      }

    } catch (error) {
      console.error('Error fetching contract-specific data:', error);
      showError('Failed to Load Data', 'Could not load contract-specific details.', 'fetchContractDataError');
    } finally {
      hideLoading();
    }
  }
}, [form, priorities, showError, showLoading, extractArrayData, allTicketCategories]);

  // Fetch static data for form dropdowns
  useEffect(() => {
    const fetchStaticData = async () => {
      const loadingKey = 'fetchStaticDataLoading';
      const hideLoading = showLoading(loadingKey, 'Loading form options...');

      try {
        let contractsRes;
        if (permissions.canEdit('tickets', PERMISSION_LEVELS.ALL)) {
          contractsRes = await api.get('/api/contracts');
        } else if (user?.organizationId) {
          contractsRes = await api.get(`/api/contracts`);
        } else {
          contractsRes = { data: [] };
        }

        const [
          ticketCategoriesRes,
          prioritiesRes,
          ticketTypesRes,
          productCategoriesRes,
          productsRes,
          ticketStatusesRes,
          assigneesRes,
        ] = await Promise.all([
          api.get('/api/ticket-categories'),
          api.get('/api/priorities'),
          api.get('/api/ticket-types'),
          api.get('/api/product-categories'),
          api.get('/api/products'),
          api.get('/api/ticket-statuses'),
          api.get('/api/users/active-internal-and-partner'),
        ]);

        setContracts(extractArrayData(contractsRes, 'contracts'));
        const fetchedPriorities = extractArrayData(prioritiesRes, 'priorities');
        setPriorities(fetchedPriorities);
        setDisplayedPriorities(fetchedPriorities);

        const allCategories = extractArrayData(ticketCategoriesRes, 'ticketCategories');
        setAllTicketCategories(allCategories);
        setTicketCategories(allCategories);

        setProductCategories(extractArrayData(productCategoriesRes, 'productCategories'));
        setProducts(extractArrayData(productsRes, 'products'));

        const fetchedTicketTypes = extractArrayData(ticketTypesRes, 'ticketTypes');
        const customTypeOrder = ["Corrective Maintenance", "Preventive Maintenance", "Others"];
        const sortedTicketTypes = fetchedTicketTypes.sort((a, b) => {
          const indexA = customTypeOrder.indexOf(a.name);
          const indexB = customTypeOrder.indexOf(b.name);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.name.localeCompare(b.name);
        });
        setTicketTypes(sortedTicketTypes);

        setTicketStatuses(extractArrayData(ticketStatusesRes, 'ticketStatuses'));
        setAssignees(extractArrayData(assigneesRes, 'users'));
        
        setStaticDataLoaded(true);
        hideLoading();
      } catch (error) {
        hideLoading();
        console.error('Error fetching static data for EditTicketForm:', error);
        showError('Failed to Load Form Options', error.response?.data?.message || 'An unexpected error occurred while loading form options.', 'fetchStaticDataError');
      }
    };
    fetchStaticData();
  }, [extractArrayData, user, permissions, showError, showSuccess, showLoading]);

  // Filter products when product category changes
  useEffect(() => {
    if (selectedProductCategoryId && contractProducts.length > 0) {
      let productsForCategory = contractProducts.filter(p => 
        String(p.productCategoryId) === String(selectedProductCategoryId)
      );

      // Always include the current displayProduct if it matches the category and isn't in the list
      if (displayProduct && String(displayProduct.productCategoryId) === String(selectedProductCategoryId) &&
          !productsForCategory.some(p => String(p.id) === String(displayProduct.id))) {
        productsForCategory = [...productsForCategory, displayProduct];
      }

      setInternalFilteredProducts(productsForCategory);
      
      // Only reset productId if we're not currently loading ticket data
      if (!ticketLoading) {
        const currentProductId = form.getFieldValue('productId');
        const productExists = productsForCategory.some(p => 
          String(p.id) === String(currentProductId)
        );
        if (!productExists) {
          form.setFieldsValue({ productId: undefined });
        }
      }
    } else {
      setInternalFilteredProducts([]);
    }
  }, [selectedProductCategoryId, contractProducts, form, ticketLoading, displayProduct]);

  // Handle initial product setup
  useEffect(() => {
    if (ticketData && contractProducts.length > 0) {
      const productId = ticketData.productId;
      if (productId) {
        let currentProduct = contractProducts.find(p => String(p.id) === String(productId));
        if (!currentProduct && displayProduct && String(displayProduct.id) === String(productId)) {
          currentProduct = displayProduct;
        }
        if (currentProduct && currentProduct.productCategoryId) {
          setSelectedProductCategoryId(currentProduct.productCategoryId);
          let productsForCategory = contractProducts.filter(p => 
            String(p.productCategoryId) === String(currentProduct.productCategoryId)
          );
          if (!productsForCategory.some(p => String(p.id) === String(productId))) {
            productsForCategory = [...productsForCategory, currentProduct];
          }
          setInternalFilteredProducts(productsForCategory);
          setTimeout(() => {
            form.setFieldsValue({
              productCategoryId: currentProduct.productCategoryId,
              productId: productId
            });
          }, 100);
        }
      }
    }
  }, [ticketData, contractProducts, form, displayProduct]);

  // Fetch ticket data
  useEffect(() => {
    const fetchTicketData = async () => { 
      if (!ticketId) {
        setTicketLoading(false);
        return;
      }

      setTicketLoading(true);
      const loadingKey = 'fetchTicketDataLoading';
      const hideLoading = showLoading(loadingKey, `Loading ticket ${ticketId}...`);

      try {
        const ticketResponse = await api.get(`/api/tickets/${ticketId}`);
        
        if (!ticketResponse.data.success) {
          throw new Error(ticketResponse.data.message || 'Failed to fetch ticket');
        }

        const ticketDataFromServer = convertSnakeToCamel(ticketResponse.data.data); 

        // Extract product information from the response
        const productData = {
          id: ticketDataFromServer.productId,
          name: ticketDataFromServer.productName,
          code: ticketDataFromServer.productCode,
          productCategoryId: ticketDataFromServer.productCategoryId
        };

        setTicketData(ticketDataFromServer);
        setDisplayProduct(productData);
        setOriginalStatusId(ticketDataFromServer.currentStatusId); 
        setDescriptionHtml(ticketDataFromServer.description || '<p></p>');
        setSelectedContractId(ticketDataFromServer.contractId);

        // Wait for static data to be loaded before setting form values
        if (staticDataLoaded) {
          form.setFieldsValue({
            contractId: ticketDataFromServer.contractId,
            ticketCategoryId: ticketDataFromServer.ticketCategoryId,
            productCategoryId: ticketDataFromServer.productCategoryId,
            productId: ticketDataFromServer.productId,
            title: ticketDataFromServer.title,
            caseReportedDate: ticketDataFromServer.caseReportedDate ? dayjs(ticketDataFromServer.caseReportedDate) : undefined,
            dueDate: ticketDataFromServer.dueDate ? dayjs(ticketDataFromServer.dueDate) : undefined,
            agreedResolutionPeriodValue: ticketDataFromServer.agreedResolutionPeriodValue,
            agreedToExtendTime: ticketDataFromServer.agreedToExtendTime === 1,
            priorityId: ticketDataFromServer.priorityId,
            ticketTypeId: ticketDataFromServer.ticketTypeId,
            currentStatusId: ticketDataFromServer.currentStatusId || undefined,
            assignedToUserId: ticketDataFromServer.assignedToUserId || undefined,
            internalDescription: ticketDataFromServer.internalDescription,
            resolutionTaken: ticketDataFromServer.resolutionTaken,
            responseType: ticketDataFromServer.responseType,
            cause: ticketDataFromServer.cause,
            impact: ticketDataFromServer.impact,
            remedialActions: ticketDataFromServer.remedialActions,
            proposedResolveDate: ticketDataFromServer.proposedResolveDate
              ? dayjs(ticketDataFromServer.proposedResolveDate)
              : dayjs().tz('Asia/Kuala_Lumpur'),
            proposedEndDate: ticketDataFromServer.proposedEndDate
              ? dayjs(ticketDataFromServer.proposedEndDate)
              : dayjs().tz('Asia/Kuala_Lumpur'),
          });

          const initialProductCategoryId = ticketDataFromServer.productCategoryId;
          if (initialProductCategoryId) {
              handleProductCategoryChange(initialProductCategoryId);
          }

          // Fetch contract-specific data after setting initial values
          if (ticketDataFromServer.contractId) {
            handleContractChange(ticketDataFromServer.contractId);
          }
        }

        setExistingAttachments(ticketDataFromServer.attachments || []);

        hideLoading();
        showSuccess('Ticket Loaded', `Ticket ${ticketId} fetched successfully!`, 'fetchTicketDataSuccess');

      } catch (error) {
        hideLoading();
        console.error('Error fetching ticket data:', error); 
        setTicketError(error.response?.data?.message || 'Failed to load ticket.');
        showError('Failed to Load Ticket', error.response?.data?.message || `An unexpected error occurred while loading ticket ${ticketId}.`, 'fetchTicketDataError');
        if (error.response?.status === 404 || error.response?.status === 403) {
          navigate('/tickets');
        }
      } finally {
        setTicketLoading(false);
      }
    };

    if (staticDataLoaded) {
      fetchTicketData();
    }
  }, [ticketId, form, navigate, showError, showSuccess, showLoading, fetchProductForDisplay, staticDataLoaded, handleContractChange]);

  // Set form values after both static data and ticket data are loaded
  useEffect(() => {
    if (staticDataLoaded && ticketData) {
      form.setFieldsValue({
        contractId: ticketData.contractId,
        ticketCategoryId: ticketData.ticketCategoryId,
        productCategoryId: ticketData.productCategoryId,
        productId: ticketData.productId,
        title: ticketData.title,
        caseReportedDate: ticketData.caseReportedDate ? dayjs(ticketData.caseReportedDate) : undefined,
        dueDate: ticketData.dueDate ? dayjs(ticketData.dueDate) : undefined,
        agreedResolutionPeriodValue: ticketData.agreedResolutionPeriodValue,
        agreedToExtendTime: ticketData.agreedToExtendTime === 1,
        priorityId: ticketData.priorityId,
        ticketTypeId: ticketData.ticketTypeId,
        currentStatusId: ticketData.currentStatusId || undefined,
        assignedToUserId: ticketData.assignedToUserId || undefined,
        internalDescription: ticketData.internalDescription,
        resolutionTaken: ticketData.resolutionTaken,
        responseType: ticketData.responseType,
        cause: ticketData.cause,
        impact: ticketData.impact,
        remedialActions: ticketData.remedialActions,
        proposedResolveDate: ticketData.proposedResolveDate
              ? dayjs(ticketData.proposedResolveDate)
              : dayjs().tz('Asia/Kuala_Lumpur'),
        proposedEndDate: ticketData.proposedEndDate
              ? dayjs(ticketData.proposedEndDate)
              : dayjs().tz('Asia/Kuala_Lumpur'),
      });

      if (ticketData.productCategoryId) {
        handleProductCategoryChange(ticketData.productCategoryId);
      }
    }
  }, [staticDataLoaded, ticketData, form]);

  // Fetch comments
// Update the fetchComments function to ensure proper user data mapping
const fetchComments = useCallback(async () => {
    if (!ticketId) return;
    
    const loadingKey = 'fetchCommentsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading comments...');
    
    try {
        const response = await api.get(`/api/tickets/${ticketId}/comments`);
        console.log('[DEBUG] Fetched comments response:', response.data);
        
        // Ensure proper user data mapping
        const formattedComments = (response.data?.comments || []).map(comment => ({
            id: comment.id,
            ticketId: comment.ticket_id || comment.ticketId,
            commentText: comment.comment_text || comment.commentText || '',
            userId: comment.user_id || comment.userId,
            isSystemEvent: Boolean(comment.is_system_event || comment.isSystemEvent),
            eventType: comment.event_type || comment.eventType,
            oldValue: comment.old_value || comment.oldValue,
            newValue: comment.new_value || comment.newValue,
            createdAt: comment.created_at || comment.createdAt,
            updatedAt: comment.updated_at || comment.updatedAt,
            user: comment.user ? {
                id: comment.user.id,
                firstName: comment.user.first_name || comment.user.firstName || 'Unknown',
                lastName: comment.user.last_name || comment.user.lastName || '',
                email: comment.user.email || 'system@default.com'
            } : (comment.user_id ? {
                id: comment.user_id,
                firstName: comment.user_first_name || 'Unknown',
                lastName: comment.user_last_name || '',
                email: comment.user_email || 'system@default.com'
            } : null),
            replies: (comment.replies || []).map(reply => ({
                id: reply.id,
                content: reply.content || '',
                userId: reply.user_id || reply.userId,
                createdAt: reply.created_at || reply.createdAt,
                updatedAt: reply.updated_at || reply.updatedAt,
                userName: reply.user_name || (reply.user_first_name && reply.user_last_name 
                    ? `${reply.user_first_name} ${reply.user_last_name}` 
                    : `User ID ${reply.user_id || reply.userId} (Unknown)`)
            }))
        })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setComments(formattedComments);
        hideLoading();
    } catch (error) {
        hideLoading();
        console.error('Error fetching comments:', error);
        
        if (error.response?.status === 404) {
            navigate('/tickets');
            showError('Ticket Not Found', 'The requested ticket does not exist', 'ticketNotFoundError');
        } else {
            showError(
                'Failed to Load Comments', 
                error.response?.data?.message || 'An unexpected error occurred while loading comments.',
                'fetchCommentsError'
            );
        }
    }
}, [ticketId, navigate, showError, showSuccess, showLoading]);

  useEffect(() => {
    if (ticketId) {
      fetchComments();
    }
  }, [ticketId, fetchComments]); 

  // Filter products based on form values
  useEffect(() => {
    const currentProductCategoryId = form.getFieldValue('productCategoryId');
    if (currentProductCategoryId && contractProducts.length > 0) {
      const newFilteredProducts = contractProducts.filter(product =>
        String(product.productCategoryId) === String(currentProductCategoryId)
      );
      setInternalFilteredProducts(newFilteredProducts);
    } else {
      setInternalFilteredProducts([]);
    }
  }, [form, contractProducts]);

  // Filter priorities based on ticket category
  useEffect(() => {
    const currentTicketCategoryId = form.getFieldValue('ticketCategoryId');
    if (currentTicketCategoryId && ticketCategories.length > 0 && priorities.length > 0) {
      const selectedCategory = ticketCategories.find(cat => cat.id === currentTicketCategoryId);
      if (selectedCategory && Array.isArray(selectedCategory.assignedPriorities)) {
        const prioritiesToDisplay = selectedCategory.assignedPriorities.filter(
          assignedPri => priorities.some(mainPri => mainPri.id === assignedPri.id)
        );
        setDisplayedPriorities(prioritiesToDisplay.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      } else {
        setDisplayedPriorities([...priorities]);
      }
    } else {
      setDisplayedPriorities([...priorities]);
    }
  }, [form, ticketCategories, priorities]);

  // Handle product category change
  const handleProductCategoryChange = useCallback((selectedProductCategoryId) => {
    form.setFieldsValue({ productId: undefined });
    setSelectedProductCategoryId(selectedProductCategoryId);

    if (selectedProductCategoryId && contractProducts.length > 0) {
      const newFilteredProducts = contractProducts.filter(product =>
        String(product.productCategoryId) === String(selectedProductCategoryId)
      );
      setInternalFilteredProducts(newFilteredProducts);
    } else {
      setInternalFilteredProducts([]);
    }
  }, [form, contractProducts]);

  // Handle ticket category change
  const handleTicketCategoryChange = useCallback((selectedTicketCategoryId) => {
    form.setFieldsValue({
      priorityId: undefined,
      agreedResolutionPeriodValue: undefined
    });

    const selectedCategory = Array.isArray(ticketCategories)
      ? ticketCategories.find(cat => cat.id === selectedTicketCategoryId)
      : null;

    let autoResolutionPeriod = undefined;
    let defaultPriorityId = null;
    let prioritiesToDisplay = [];

    if (selectedCategory) {
      defaultPriorityId = selectedCategory.defaultPriorityId;
      autoResolutionPeriod = selectedCategory.defaultResolutionTimeValue;

      if (Array.isArray(selectedCategory.assignedPriorities) && selectedCategory.assignedPriorities.length > 0) {
        prioritiesToDisplay = selectedCategory.assignedPriorities.filter(
          assignedPri => priorities.some(mainPri => mainPri.id === assignedPri.id)
        );
      } else {
        const defaultPri = priorities.find(p => p.id === defaultPriorityId);
        if (defaultPri) {
          prioritiesToDisplay.push(defaultPri);
        } else {
          prioritiesToDisplay = Array.isArray(priorities) ? [...priorities] : [];
        }
      }
    } else {
      prioritiesToDisplay = Array.isArray(priorities) ? [...priorities] : [];
    }

    const sortedPrioritiesToDisplay = prioritiesToDisplay.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    setDisplayedPriorities(sortedPrioritiesToDisplay);

    if (autoResolutionPeriod !== undefined) {
      form.setFieldsValue({ agreedResolutionPeriodValue: autoResolutionPeriod });
    }

    if (selectedCategory && defaultPriorityId) {
      const defaultPriorityObject = sortedPrioritiesToDisplay.find(p => p.id === defaultPriorityId);
      if (defaultPriorityObject) {
        form.setFieldsValue({ priorityId: defaultPriorityObject.id });
      }
    }
  }, [form, ticketCategories, priorities]);

  // Clear description in editor
  const handleClearDescription = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.setContent('<p></p>');
      setDescriptionHtml('<p></p>');
      message.info('Description cleared.');
    }
  }, []);

  // Handle file upload changes
  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);
  }, []);

  // Dummy upload request
  const dummyRequest = useCallback(({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  }, []);

  // Delete existing attachment
  const handleDeleteExistingAttachment = useCallback((attachmentId) => {
    setAttachmentToDelete(attachmentId);
    setDeleteModalVisible(true);
  }, []);

  // Add this function to handle the actual deletion
const handleConfirmDelete = useCallback(async () => {
  if (!attachmentToDelete) return;
  
  const loadingKey = `deleteAttachment_${attachmentToDelete}`;
  const hideLoading = showLoading(loadingKey, 'Deleting attachment...');
  
  try {
    const response = await api.delete(`/api/tickets/${ticketId}/attachments/${attachmentToDelete}`);
    
    // Handle 204 No Content response (successful delete)
    if (response.status === 204) {
      // Refresh attachments after successful deletion
      const attachmentsRes = await api.get(`/api/tickets/${ticketId}/attachments`);
      let updatedAttachments = [];
      
      // Handle different response formats
      if (attachmentsRes.data && Array.isArray(attachmentsRes.data)) {
        updatedAttachments = attachmentsRes.data;
      } else if (attachmentsRes.data && attachmentsRes.data.attachments && Array.isArray(attachmentsRes.data.attachments)) {
        updatedAttachments = attachmentsRes.data.attachments;
      } else if (attachmentsRes.data && attachmentsRes.data.data && Array.isArray(attachmentsRes.data.data)) {
        updatedAttachments = attachmentsRes.data.data;
      }
      
      const formattedAttachments = updatedAttachments.map(attachment => convertSnakeToCamel(attachment));
      setExistingAttachments(formattedAttachments);
      
      hideLoading();
      showSuccess('Attachment Deleted', 'Attachment deleted successfully.', loadingKey);
      return;
    }
    
    // Handle other success responses (if backend returns JSON)
    if (response.data && response.data.success) {
      // Refresh attachments after successful deletion
      const attachmentsRes = await api.get(`/api/tickets/${ticketId}/attachments`);
      let updatedAttachments = [];
      
      if (attachmentsRes.data && Array.isArray(attachmentsRes.data)) {
        updatedAttachments = attachmentsRes.data;
      } else if (attachmentsRes.data && attachmentsRes.data.attachments && Array.isArray(attachmentsRes.data.attachments)) {
        updatedAttachments = attachmentsRes.data.attachments;
      } else if (attachmentsRes.data && attachmentsRes.data.data && Array.isArray(attachmentsRes.data.data)) {
        updatedAttachments = attachmentsRes.data.data;
      }
      
      const formattedAttachments = updatedAttachments.map(attachment => convertSnakeToCamel(attachment));
      setExistingAttachments(formattedAttachments);
      
      hideLoading();
      showSuccess('Attachment Deleted', 'Attachment deleted successfully.', loadingKey);
    } else {
      throw new Error(response.data?.message || 'Failed to delete attachment');
    }
  } catch (error) {
    hideLoading();
    console.error('Error deleting attachment:', error);
    
    // Check if it's a 204 response that was misinterpreted
    if (error.response?.status === 204) {
      // This is actually a success - refresh attachments
      const attachmentsRes = await api.get(`/api/tickets/${ticketId}/attachments`);
      let updatedAttachments = [];
      
      if (attachmentsRes.data && Array.isArray(attachmentsRes.data)) {
        updatedAttachments = attachmentsRes.data;
      } else if (attachmentsRes.data && attachmentsRes.data.attachments && Array.isArray(attachmentsRes.data.attachments)) {
        updatedAttachments = attachmentsRes.data.attachments;
      } else if (attachmentsRes.data && attachmentsRes.data.data && Array.isArray(attachmentsRes.data.data)) {
        updatedAttachments = attachmentsRes.data.data;
      }
      
      const formattedAttachments = updatedAttachments.map(attachment => convertSnakeToCamel(attachment));
      setExistingAttachments(formattedAttachments);
      
      showSuccess('Attachment Deleted', 'Attachment deleted successfully.', loadingKey);
    } else if (error.response?.status === 403) {
      showError(
        'Permission Denied', 
        'You do not have permission to delete this attachment.',
        loadingKey
      );
    } else {
      showError(
        'Failed to Delete Attachment', 
        error.response?.data?.message || 'An unexpected error occurred while deleting the attachment.', 
        loadingKey
      );
    }
  } finally {
    setDeleteModalVisible(false);
    setAttachmentToDelete(null);
  }
}, [attachmentToDelete, ticketId, showLoading, showSuccess, showError, convertSnakeToCamel]);

  const handleCancelDelete = useCallback(() => {
    setDeleteModalVisible(false);
    setAttachmentToDelete(null);
  }, []);

// Submit ticket update
const submitTicketUpdate = useCallback(async (values, comment = null) => {
  
  console.log('Submitting ticket update with values:', values);
  console.log('Current user:', {
    id: user.id,
    role: user.roleName,
    organizationId: user.organizationId
  });
  console.log('Ticket data:', {
    id: ticketData.id,
    createdByUserId: ticketData.createdByUserId,
    assignedToUserId: ticketData.assignedToUserId,
    organizationId: ticketData.organizationId
  });

  setLoading(true);
  const loadingKey = 'ticketUpdateLoading';
  const hideLoading = showLoading(loadingKey, `Updating ticket ${ticketId}...`);

  try {
    const formData = new FormData();

    // Convert all field names from camelCase to snake_case for backend
    const snakeCaseValues = convertCamelToSnake(values);
    
    // Handle special case for assigned_to_user_id which might be null
    if (snakeCaseValues.assigned_to_user_id === null) {
      // Convert null to empty string for backend to handle unassignment
      snakeCaseValues.assigned_to_user_id = '';
    }

    for (const key in snakeCaseValues) {
      if (snakeCaseValues.hasOwnProperty(key)) {
        let value = snakeCaseValues[key];

        // Handle date fields consistently
        if (dayjs.isDayjs(value)) {
          value = value.tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss');
        }

        if (key === 'agreed_to_extend_time') {
          value = (value === true || value === '1') ? '1' : '0';
        } else if (typeof value === 'boolean') {
          value = value ? '1' : '0';
        }

        // Skip undefined values but allow empty strings (for unassignment)
        if (value !== undefined) {
          formData.append(key, value);
        }
      }
    }

    // Add description with correct field name
    formData.append('description', descriptionHtml);
    
    // Add attachments
    fileList.forEach(file => {
      if (file.originFileObj) {
        formData.append('attachments', file.originFileObj);
      }
    });

    const ticketUpdateResponse = await api.put(`/api/tickets/${ticketId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (comment && comment.trim() !== '') {
      const oldStatusName = ticketStatuses.find(s => s.id === originalStatusId)?.name || 'Unknown Status';
      const newStatusName = ticketStatuses.find(s => s.id === newStatusIdForComment)?.name || 'Unknown Status';

      const commentTextForBackend = `Ticket Status changed from "${oldStatusName}" to "${newStatusName}". Comment: ${comment}`;

      const commentPayload = convertCamelToSnake({
        commentText: commentTextForBackend,
        isSystemEvent: 0,
        eventType: 'status_change_manual',
        oldValue: originalStatusId,
        newValue: newStatusIdForComment
      });
      
      try {
        await api.post(`/api/tickets/${ticketId}/comments`, commentPayload);
      } catch (commentError) {
        console.error("Error submitting comment:", commentError);
        showError('Failed to Add Comment', "Ticket updated, but failed to add the associated comment.", loadingKey);
      }
    }

    hideLoading();
    showSuccess('Ticket Updated', 'Ticket updated successfully!', loadingKey);
    setFileList([]);

    // Refresh data - ONLY refresh ticket data and attachments
    const [updatedTicketResponse, attachmentsRes] = await Promise.all([
      api.get(`/api/tickets/${ticketId}`),
      api.get(`/api/tickets/${ticketId}/attachments`)
    ]);

    const updatedTicketData = convertSnakeToCamel(updatedTicketResponse.data.data);
    setTicketData(updatedTicketData);
    setOriginalStatusId(updatedTicketData.currentStatusId);
    
    // Set form values with proper handling for null assignee
    form.setFieldsValue({
      currentStatusId: updatedTicketData.currentStatusId || undefined,
      assignedToUserId: updatedTicketData.assignedToUserId !== null ? updatedTicketData.assignedToUserId : null,
    });

    // Refresh comments separately to ensure they're properly formatted
    try {
      await fetchComments(); // This will properly set the comments state
    } catch (commentError) {
      console.error("Error refreshing comments:", commentError);
      // Don't fail the whole update if comments refresh fails
    }
    
    // Refresh attachments
    let updatedAttachments = [];
    if (attachmentsRes.data && Array.isArray(attachmentsRes.data)) {
      updatedAttachments = attachmentsRes.data;
    } else if (attachmentsRes.data && attachmentsRes.data.attachments && Array.isArray(attachmentsRes.data.attachments)) {
      updatedAttachments = attachmentsRes.data.attachments;
    } else if (attachmentsRes.data && attachmentsRes.data.data && Array.isArray(attachmentsRes.data.data)) {
      updatedAttachments = attachmentsRes.data.data;
    }
    
    const formattedAttachments = updatedAttachments.map(attachment => convertSnakeToCamel(attachment));
    setExistingAttachments(formattedAttachments);

  } catch (error) {
    hideLoading();
    console.error('Error updating ticket or submitting comment:', error);
    let errorMessage = 'Failed to update ticket. Please try again.';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    showError('Update Failed', errorMessage, loadingKey);
  } finally {
    setLoading(false);
  }
}, [
  ticketId,
  descriptionHtml,
  fileList,
  commentText,
  newStatusIdForComment,
  originalStatusId,
  ticketStatuses,
  form,
  showLoading,
  showSuccess,
  showError,
  setTicketData,
  setOriginalStatusId,
  setFileList,
  setComments,
  setExistingAttachments,
  extractArrayData,
  ticketData,
  fetchComments,
  convertCamelToSnake,
  convertSnakeToCamel
]);

    // Update form fields when tempTicketData changes
    useEffect(() => {
      if (isDateModalVisible) {
        const initialCaseReportedDate = tempTicketData?.caseReportedDate ? dayjs(tempTicketData.caseReportedDate) : dayjs().tz('Asia/Kuala_Lumpur');
        const initialClosedAt = tempTicketData?.closedAt ? dayjs(tempTicketData.closedAt) : dayjs().tz('Asia/Kuala_Lumpur');
        console.log('Initializing caseReportedDate:', initialCaseReportedDate.format('YYYY-MM-DD hh:mm A')); // Debug log
        console.log('Initializing closedAt:', initialClosedAt.format('YYYY-MM-DD hh:mm A')); // Debug log
        setCaseReportedDate(initialCaseReportedDate);
        setClosedAt(initialClosedAt);
        dateForm.setFieldsValue({
          caseReportedDate: initialCaseReportedDate,
          closedAt: initialClosedAt,
        });
      }
    }, [isDateModalVisible, tempTicketData, dateForm]);

    // Handle form submission
    const handleFormSubmit = useCallback(async (values) => {
      if (ticketLoading || !ticketData) {
        console.warn('Ticket data not yet loaded. Aborting submission.');
        showError('Submission Aborted', 'Ticket data is still loading. Please wait and try again.', 'submitAborted');
        return;
      }

      const currentStatusInForm = values.currentStatusId;

      if (currentStatusInForm === CLOSED_STATUS_ID && ticketData.currentStatusId !== CLOSED_STATUS_ID) {
        setTempTicketData(values);
        setNewStatusIdForComment(currentStatusInForm);
        setIsCommentModalVisible(true);
      } else if (currentStatusInForm !== originalStatusId && originalStatusId !== null && originalStatusId !== undefined) {
        setTempTicketData(values);
        setNewStatusIdForComment(currentStatusInForm);
        setIsCommentModalVisible(true);
      } else {
        submitTicketUpdate(values);
      }
    }, [ticketLoading, ticketData, originalStatusId, submitTicketUpdate, setNewStatusIdForComment, setIsCommentModalVisible, showError]);

    // Handle form submission failure
    const handleFormSubmissionFailed = useCallback((errorInfo) => {
      console.error('Form validation failed!', errorInfo);
      showError('Validation Error', 'Please check all required fields and correct any errors.', 'formValidationError');
    }, [showError]);

    // Handle comment modal OK
    const handleCommentModalOk = useCallback(async () => {
      const loadingKey = 'commentModalUpdateLoading';
      const hideLoading = showLoading(loadingKey, 'Updating ticket with comment...');

      if (commentText.trim() === '') {
        hideLoading();
        showError('Comment Required', 'A comment is required to update ticket status.', loadingKey);
        return;
      }

      setIsCommentModalVisible(false);
      const values = tempTicketData;
      try {
        if (values.currentStatusId === CLOSED_STATUS_ID) {
          setIsDateModalVisible(true);
        } else {
          await submitTicketUpdate(values, commentText);
          setCommentText('');
          setNewStatusIdForComment(null);
          setTempTicketData(null);
        }
      } catch (error) {
        console.error('Error in handleCommentModalOk:', error);
        showError('Failed to Update', error.response?.data?.message || 'An unexpected error occurred while updating the ticket status with your comment.', loadingKey);
      } finally {
        hideLoading();
        setTimeout(() => hideLoading(), 5000);
      }
    }, [commentText, tempTicketData, submitTicketUpdate, setIsCommentModalVisible, setNewStatusIdForComment, showLoading, showError]);

    // Handle comment modal cancel
    const handleCommentModalCancel = useCallback(() => {
      setIsCommentModalVisible(false);
      setCommentText('');
      setNewStatusIdForComment(null);
      setTempTicketData(null);
      form.setFieldsValue({ currentStatusId: originalStatusId });
      showSuccess('Status Change Cancelled', 'The status change was cancelled and reverted.', 'statusChangeCancelled');
    }, [form, originalStatusId, setIsCommentModalVisible, setCommentText, setNewStatusIdForComment, showSuccess]);

    // Handle date modal OK
    const handleDateModalOk = () => {
      dateForm.validateFields()
        .then((dateValues) => {
          console.log('Submitting dateValues:', {
            caseReportedDate: dateValues.caseReportedDate?.format('YYYY-MM-DD hh:mm A'),
            closedAt: dateValues.closedAt?.format('YYYY-MM-DD hh:mm A'),
          }); // Debug log
          const updatedTicketData = {
            ...tempTicketData,
            caseReportedDate: dateValues.caseReportedDate
              ? dateValues.caseReportedDate.tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss')
              : caseReportedDate.tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss'),
            closedAt: dateValues.closedAt
              ? dateValues.closedAt.tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss')
              : closedAt.tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss'),
          };
          console.log('Sending to submitTicketUpdate:', updatedTicketData); // Debug log
          submitTicketUpdate(updatedTicketData, commentText);
          setIsDateModalVisible(false);
          dateForm.resetFields();
          setTempTicketData(null);
          setCommentText('');
          setNewStatusIdForComment(null);
          setCaseReportedDate(null);
          setClosedAt(dayjs().tz('Asia/Kuala_Lumpur'));
        })
        .catch((errorInfo) => {
          console.error('Validation Failed:', errorInfo);
          showError('Validation Failed', 'Please ensure all date fields are filled correctly.', 'dateValidationError');
        });
    };

    // Handle date modal cancel
    const handleDateModalCancel = () => {
      setIsDateModalVisible(false);
      dateForm.resetFields();
      setTempTicketData(null);
      setCommentText('');
      setNewStatusIdForComment(null);
      form.setFieldsValue({ currentStatusId: originalStatusId });
      setCaseReportedDate(null);
      setClosedAt(dayjs().tz('Asia/Kuala_Lumpur'));
      showSuccess('Status Change Cancelled', 'The status change was cancelled and reverted.', 'statusChangeCancelled');
    };

  // Add general comment
  const handleAddGeneralComment = useCallback(async () => {
    if (newGeneralCommentText.trim() === '') {
      showError('Comment Empty', 'Please enter a comment before adding.', 'emptyGeneralComment');
      return;
    }
    setLoading(true);
    const loadingKey = 'addGeneralCommentLoading';
    const hideLoading = showLoading(loadingKey, 'Adding general comment...');

    try {
      const commentPayload = convertCamelToSnake({
        commentText: newGeneralCommentText,
        isSystemEvent: false,
        eventType: 'manual_comment',
      });
      await api.post(`/api/tickets/${ticketId}/comments`, commentPayload);

      hideLoading();
      showSuccess('Comment Added', 'Comment added successfully!', loadingKey);
      setNewGeneralCommentText('');
      fetchComments();
    } catch (error) {
      hideLoading();
      console.error('Error adding general comment:', error);
      showError('Failed to Add Comment', error.response?.data?.message || 'An unexpected error occurred while adding the comment.', loadingKey);
    } finally {
      setLoading(false);
    }
  }, [newGeneralCommentText, ticketId, fetchComments, showLoading, showSuccess, showError]);

  // Handle reply to comment
  const handleReplyToComment = useCallback((commentId) => {
    setReplyingToCommentId(commentId);
    setReplyText('');
  }, []);

  // Send reply
  const handleSendReply = useCallback(async (parentCommentId) => {
    if (replyText.trim() === '') {
      showError('Reply Empty', 'Reply cannot be empty.', 'emptyReply');
      return;
    }
    if (!parentCommentId) {
      showError('Invalid Comment', 'No comment selected for reply.', 'invalidComment');
      return;
    }

    setLoading(true);
    const loadingKey = `sendReply_${parentCommentId}`;
    const hideLoading = showLoading(loadingKey, 'Sending reply...');

    try {
      const replyPayload = convertCamelToSnake({
        content: replyText,
      });
      console.log('[DEBUG] handleSendReply payload:', replyPayload);
      await api.post(`/api/tickets/${ticketId}/comments/${parentCommentId}/replies`, replyPayload);

      hideLoading();
      showSuccess('Reply Added', 'Reply added successfully!', loadingKey);
      setReplyText('');
      setReplyingToCommentId(null);
      fetchComments();
    } catch (error) {
      hideLoading();
      console.error('Error adding reply:', error);
      showError('Failed to Add Reply', error.response?.data?.message || 'An unexpected error occurred while adding the reply.', loadingKey);
    } finally {
      setLoading(false);
    }
  }, [replyText, ticketId, fetchComments, showLoading, showSuccess, showError]);

  // Cancel reply
  const handleCancelReply = useCallback(() => {
    setReplyingToCommentId(null);
    setReplyText('');
  }, []);

  // Edit comment
  const handleEditComment = useCallback((comment) => {
    setEditingComment(comment);
    setEditingCommentText(comment.commentText);
    setIsEditCommentModalVisible(true);
  }, []);

  // Update comment
  const handleUpdateComment = useCallback(async () => {
    if (!editingComment || !editingComment.id || editingCommentText.trim() === '') {
      showError('Comment Empty', 'Edited comment cannot be empty or invalid.', 'emptyEditedComment');
      return;
    }
    setLoading(true);
    const loadingKey = `updateComment_${editingComment.id}`;
    const hideLoading = showLoading(loadingKey, 'Updating comment...');

    try {
      const commentPayload = convertCamelToSnake({
        commentText: editingCommentText,
      });
      console.log('[DEBUG] updateComment payload:', commentPayload); // Debug log
      await api.put(`/api/tickets/${ticketId}/comments/${editingComment.id}`, commentPayload);

      hideLoading();
      showSuccess('Comment Updated', 'Comment updated successfully!', loadingKey);
      setIsEditCommentModalVisible(false);
      setEditingComment(null);
      setEditingCommentText('');
      fetchComments();
    } catch (error) {
      hideLoading();
      console.error('Error updating comment:', error);
      showError('Failed to Update Comment', error.response?.data?.message || 'An unexpected error occurred while updating the comment.', loadingKey);
    } finally {
      setLoading(false);
    }
  }, [editingComment, editingCommentText, ticketId, fetchComments, setIsEditCommentModalVisible, setEditingComment, setEditingCommentText, showLoading, showSuccess, showError]);

  // Cancel edit comment
  const handleCancelEditComment = useCallback(() => {
    setIsEditCommentModalVisible(false);
    setEditingComment(null);
    setEditingCommentText('');
  }, []);

// Simplify the handleDeleteComment function
const handleDeleteComment = useCallback(
  async (commentId) => {
    console.log('[DEBUG] Delete button clicked for commentId:', commentId);
    
    if (!ticketId) {
      showError('Error', 'Ticket ID is missing. Please refresh and try again.');
      return;
    }

    // Show confirmation modal first
    setCommentToDelete(commentId);
    setDeleteCommentModalVisible(true);
  },
  [ticketId, showError]
);

// Add a separate function to handle the actual deletion
const handleConfirmDeleteComment = useCallback(async () => {
  if (!commentToDelete) return;
  
  setLoading(true);
  const loadingKey = `deleteComment_${commentToDelete}`;
  const hideLoading = showLoading(loadingKey, 'Deleting comment...');

  try {
    const response = await api.delete(`/api/tickets/${ticketId}/comments/${commentToDelete}`);
    console.log('[DEBUG] Delete API success:', response.data);
    
    hideLoading();
    showSuccess('Comment Deleted', 'Comment deleted successfully!', loadingKey);
    
    // Refresh comments
    await fetchComments();
    
  } catch (error) {
    console.error('[DEBUG] Error deleting comment:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    
    hideLoading();
    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred.';
    showError('Failed to Delete Comment', errorMessage, loadingKey);
  } finally {
    setLoading(false);
    setDeleteCommentModalVisible(false);
    setCommentToDelete(null);
  }
}, [commentToDelete, ticketId, fetchComments, showLoading, showSuccess, showError]);

// Update the Modal for comment deletion
<Modal
  title="Confirm Comment Deletion"
  open={deleteCommentModalVisible}
  onOk={handleConfirmDeleteComment}
  onCancel={() => {
    setDeleteCommentModalVisible(false);
    setCommentToDelete(null);
  }}
  okText="Yes, Delete"
  okType="danger"
  cancelText="Cancel"
  confirmLoading={loading}
>
  <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
</Modal>

  // Close preview modal
  const handlePreviewModalClose = useCallback(() => {
    setIsPreviewModalVisible(false);
    setPdfPreviewBuffer(null);
    setPdfPreviewTitle('');
  }, []);

// Handle report actions (preview/download)
const handleReportAction = useCallback(async (actionType, format = 'pdf') => {
  if (!ticketData || !ticketData.ticketUuid) {
    showError('Report Error', 'Ticket UUID not available for report generation.', 'noTicketUuidForReport');
    return;
  }

  const loadingKey = `reportAction_${actionType}_${format}`;
  let hideLoading;

  if (actionType === 'preview') {
    setPreviewLoading(true);
    setIsPreviewModalVisible(true);
    setPdfPreviewBuffer(null);
    setPdfPreviewTitle(`Borang Aduan - ${ticketData.ticketUuid}`);
    hideLoading = showLoading(loadingKey, 'Preparing report for preview...');
  } else if (actionType === 'download') {
    setDownloadLoading(true);
    hideLoading = showLoading(loadingKey, `Preparing Borang Aduan for download as ${format.toUpperCase()}...`);
  } else {
    showError('Invalid Action', 'Unsupported report action type.', 'invalidReportAction');
    return;
  }

  try {
    let queryParams = new URLSearchParams();
    queryParams.append('format', actionType === 'preview' ? 'pdf' : format);

    const url = `/api/reports/tickets/${ticketData.ticketUuid}/borang-aduan?${queryParams.toString()}`;

    const response = await api.get(url, { responseType: 'arraybuffer', timeout: 60000 });
    const contentType = actionType === 'preview' ? 'application/pdf' : (format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    const blob = new Blob([response.data], { type: contentType });

    const contentDispositionHeader = response.headers['content-disposition'];
    let filename = `BorangAduan_${ticketData.ticketUuid}.${actionType === 'preview' ? 'pdf' : format}`;

    if (contentDispositionHeader) {
      const filenameRegex = /filename\*=(?:UTF-\d['']*)?([^;"]+)(?:[''])?|filename=['']?([^;"]+)['']?/i;
      const filenameMatch = contentDispositionHeader.match(filenameRegex);
      if (filenameMatch) {
        let extractedFilename = '';
        if (filenameMatch[1]) {
          extractedFilename = filenameMatch[1];
          try {
            if (extractedFilename.startsWith("utf-8''")) {
              extractedFilename = extractedFilename.substring("utf-8''".length);
            }
            const decoded = decodeURIComponent(extractedFilename.replace(/\+/g, ' '));
            filename = decoded.split('.').slice(0, -1).join('.') + `.${actionType === 'preview' ? 'pdf' : format}`;
          } catch (e) {
            const cleaned = extractedFilename.replace(/%20/g, ' ');
            filename = cleaned.split('.').slice(0, -1).join('.') + `.${actionType === 'preview' ? 'pdf' : format}`;
          }
        } else if (filenameMatch[2]) {
          extractedFilename = filenameMatch[2];
          try {
            const decoded = decodeURIComponent(extractedFilename.replace(/%20/g, ' '));
            filename = decoded.split('.').slice(0, -1).join('.') + `.${actionType === 'preview' ? 'pdf' : format}`;
          } catch (e) {
            const cleaned = extractedFilename.replace(/%20/g, ' ');
            filename = cleaned.split('.').slice(0, -1).join('.') + `.${actionType === 'preview' ? 'pdf' : format}`;
          }
        }
      }
    }

    if (actionType === 'preview') {
      setPdfPreviewBuffer(response.data);
      hideLoading();
      showSuccess('Report Ready', 'Borang Aduan (PDF) data prepared for preview.', loadingKey);
    } else if (actionType === 'download') {
      const fileURL = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileURL);
      hideLoading();
      showSuccess(
        `Report Downloaded`,
        `Borang Aduan (${format.toUpperCase()}) downloaded successfully.`,
        loadingKey
      );
    }
  } catch (error) {
    if (hideLoading) hideLoading();
    console.error(`Error ${actionType}ing Borang Aduan as ${format.toUpperCase()}:`, error);
    let errorMessage = `Failed to ${actionType} Borang Aduan as ${format.toUpperCase()}.`;

    if (error.response && error.response.data) {
      try {
        const errorBlob = new Blob([error.response.data], { type: error.response.headers['content-type'] });
        const reader = new FileReader();
        reader.onload = function () {
          try {
            const errorObj = JSON.parse(reader.result);
            errorMessage = errorObj.message || errorMessage;
            if (errorObj.details) {
              errorMessage += ` Details: ${errorObj.details}`;
            }
          } catch (parseError) {
            console.warn('Failed to parse error response as JSON:', parseError);
          }
          showError('Report Error', errorMessage, loadingKey);
          if (actionType === 'preview') {
            handlePreviewModalClose();
          }
        };
        const errorContentType = error.response.headers['content-type'] || '';
        if (errorContentType.includes('application/json') || errorContentType.includes('text/plain')) {
          reader.readAsText(errorBlob);
        } else {
          showError('Report Error', errorMessage, loadingKey);
          if (actionType === 'preview') {
            handlePreviewModalClose();
          }
        }
      } catch (readError) {
        showError('Report Error', errorMessage, loadingKey);
        if (actionType === 'preview') {
          handlePreviewModalClose();
        }
      }
    } else {
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        errorMessage = `Request timed out. The server took too long to respond. Please try again.`;
      }
      showError('Report Error', errorMessage, loadingKey);
      if (actionType === 'preview') {
        handlePreviewModalClose();
      }
    }
  } finally {
    if (actionType === 'preview') {
      setPreviewLoading(false);
    } else if (actionType === 'download') {
      setDownloadLoading(false);
    }
  }
}, [ticketData, handlePreviewModalClose, showLoading, showSuccess, showError]);

  // Render comment with proper permissions
// Render comment with proper permissions
const renderComment = useCallback((comment) => {
    const isSystemEventBool = comment.isSystemEvent === 1 || comment.isSystemEvent === true;
    const userName = isSystemEventBool
      ? 'System'
      : (comment.user?.firstName && comment.user?.lastName
          ? `${comment.user.firstName} ${comment.user.lastName}`
          : (comment.user?.email || `User ID ${comment.userId} (Unknown)`));

    const createdAt = moment(comment.createdAt).format(displayDateFormat);

    let commentContent = comment.commentText;

    if (isSystemEventBool && comment.eventType === 'status_change_manual') {
        const oldStatusObj = ticketStatuses.find(s => s.id === parseInt(comment.oldValue));
        const newStatusObj = ticketStatuses.find(s => s.id === parseInt(comment.newValue));
        const oldStatusName = oldStatusObj?.name || comment.oldValue;
        const newStatusName = newStatusObj?.name || comment.newValue;

        commentContent = `Status changed manually from "${oldStatusName}" to "${newStatusName}". `;
        const autoPrefix = `Status changed from "${oldStatusName}" to "${newStatusName}". Comment: `;
        if (comment.commentText && comment.commentText.startsWith(autoPrefix)) {
            commentContent += comment.commentText.substring(autoPrefix.length).trim();
        } else {
            commentContent += comment.commentText || '';
        }
    } else if (isSystemEventBool && comment.eventType === 'status_change') {
        const oldStatusAuto = ticketStatuses.find(s => s.name === comment.oldValue)?.name || comment.oldValue;
        const newStatusAuto = ticketStatuses.find(s => s.name === comment.newValue)?.name || comment.newValue;
        commentContent = `Status changed automatically from "${oldStatusAuto || 'N/A'}" to "${newStatusAuto || 'N/A'}"`;
    } else if (isSystemEventBool && comment.eventType === 'assignment_change') {
        const oldAssignee = assignees.find(a => a.id === parseInt(comment.oldValue));
        const newAssignee = assignees.find(a => a.id === parseInt(comment.newValue));

        const oldAssigneeName = oldAssignee
          ? `${oldAssignee.firstName} ${oldAssignee.lastName}`
          : (comment.oldValue ? `User ID ${comment.oldValue} (Unassigned/User Not Found)` : 'Unassigned');

        const newAssigneeName = newAssignee
          ? `${newAssignee.firstName} ${newAssignee.lastName}`
          : (comment.newValue ? `User ID ${comment.newValue} (Assigned/User Not Found)` : 'Unassigned');

        commentContent = `Assignee changed from "${oldAssigneeName}" to "${newAssigneeName}".`;
    } else if (isSystemEventBool && comment.eventType === 'ticket_created') {
        commentContent = 'Ticket created.';
    }

    const canEditOrDeleteComment = (
        user && user.id === comment.userId ||
        permissions.canEdit('tickets', PERMISSION_LEVELS.ALL) ||
        (user && ticketData && (
            user.id === ticketData.createdByUserId ||
            user.id === ticketData.assignedToUserId
        ) && permissions.canEdit('tickets', PERMISSION_LEVELS.OWN))
    );

    return (
        <div key={comment.id} style={{ marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <Text strong>{userName}</Text>
                <Text type="secondary" style={{ fontSize: '0.85em' }}>{createdAt}</Text>
            </div>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{commentContent}</Text>

            {comment.replies && comment.replies.length > 0 && (
                <div style={{ marginLeft: '20px', borderLeft: '2px solid #f0f0f0', paddingLeft: '10px', marginTop: '10px' }}>
                    {comment.replies.map(reply => {
                        // FIXED: Properly handle reply user information
                        let replyUserName = 'Unknown User';
                        
                        // Try different ways to get the user name from reply data
                        if (reply.userName) {
                            replyUserName = reply.userName;
                        } else if (reply.user_first_name && reply.user_last_name) {
                            replyUserName = `${reply.user_first_name} ${reply.user_last_name}`;
                        } else if (reply.userFirstName && reply.userLastName) {
                            replyUserName = `${reply.userFirstName} ${reply.userLastName}`;
                        } else if (reply.user_id) {
                            // Try to find user in assignees list
                            const replyUser = assignees.find(a => a.id === parseInt(reply.user_id));
                            replyUserName = replyUser 
                                ? `${replyUser.firstName} ${replyUser.lastName}`
                                : `User ID ${reply.user_id}`;
                        } else if (reply.userId) {
                            // Try to find user in assignees list
                            const replyUser = assignees.find(a => a.id === parseInt(reply.userId));
                            replyUserName = replyUser 
                                ? `${replyUser.firstName} ${replyUser.lastName}`
                                : `User ID ${reply.userId}`;
                        }

                        const replyCreatedAt = moment(reply.createdAt).format(displayDateFormat);

                        return (
                            <div key={reply.id} style={{ marginBottom: '10px', padding: '5px', backgroundColor: '#fafafa', borderRadius: '5px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                    <Text strong>{replyUserName}</Text>
                                    <Text type="secondary" style={{ fontSize: '0.8em' }}>{replyCreatedAt}</Text>
                                </div>
                                <Text style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</Text>
                                {/* Removed Reply/Edit/Delete buttons for child comments as requested */}
                            </div>
                        );
                    })}
                </div>
            )}

            {!isSystemEventBool && (
                <div style={{ marginTop: '5px', textAlign: 'right' }}>
                    <Button
                        type="text"
                        icon={<MessageOutlined />}
                        size="small"
                        onClick={() => handleReplyToComment(comment.id)}
                        style={{ marginRight: '5px' }}
                        disabled={!canUserEditTicket}
                    >
                        Reply
                    </Button>
                    {canEditOrDeleteComment && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => handleEditComment(comment)}
                                style={{ marginRight: '5px' }}
                                disabled={!canUserEditTicket}
                            >
                                Edit
                            </Button>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => {
                                    console.log('[DEBUG] Delete button clicked for commentId:', comment.id);
                                    handleDeleteComment(comment.id);
                                }}
                                disabled={!canUserEditTicket}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </div>
            )}

            {replyingToCommentId === comment.id && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                    <TextArea
                        rows={2}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`Reply to ${userName}...`}
                        style={{ flexGrow: 1, borderRadius: '8px' }}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={() => handleSendReply(comment.id)}
                        loading={loading}
                        style={{ alignSelf: 'flex-end', borderRadius: '8px' }}
                    >
                        Send
                    </Button>
                    <Button
                        type="default"
                        onClick={handleCancelReply}
                        style={{ alignSelf: 'flex-end', borderRadius: '8px' }}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
}, [
    ticketStatuses,
    assignees,
    displayDateFormat,
    handleReplyToComment,
    handleEditComment,
    handleDeleteComment,
    replyingToCommentId,
    replyText,
    setReplyText,
    handleSendReply,
    handleCancelReply,
    loading,
    user,
    permissions,
    canUserEditTicket,
    ticketData
]);

  if (ticketLoading) {
    return (
      <div className="edit-ticket-loading">
        <Spin size="large" tip="Loading ticket data..." />
      </div>
    );
  }

  if (ticketError) {
    return (
      <Card className="edit-ticket-error-card">
        <Title level={4} type="danger">Error Loading Ticket</Title>
        <Text>{ticketError}</Text>
        <Button onClick={() => navigate('/tickets')} style={{ marginTop: '20px' }}>Back to Tickets</Button>
      </Card>
    );
  }

  return (
    <Card
      className="edit-ticket-card"
      styles={{ body: { padding: 0 } }}
    >

<div style={{ padding: '24px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
    <Title level={2} style={{ margin: 0 }}>
      Ticket # {ticketData ? ticketData.id : ''} - {ticketData ? ticketData.ticketUuid : ''}
    </Title>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
    {/* CR Management Button - Smart navigation based on CR count */}
    {ticketData?.id && (
        <Badge
            count={ticketCRs.length}
            size="small"
            offset={[-4, 0]}
            style={{
                backgroundColor: ticketCRs.length > 0 ? '#52c41a' : undefined,
                boxShadow: 'none'
            }}
        >
            <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={() => {
                    if (!canUserEditTicket) return;

                    if (ticketCRs.length === 0) {
                        // No CR → go to Create CR page with ticket pre-filled
                        navigate(`/cr-management/create?ticketId=${ticketData.id}`);
                    } else if (ticketCRs.length === 1) {
                        // Exactly 1 CR → go straight to its Detail page
                        navigate(`/cr-management/${ticketCRs[0].id}`);
                    } else {
                        // Multiple CRs → go to CR List filtered to this ticket
                        navigate(`/cr-management?ticketId=${ticketData.id}`);
                    }
                }}
                style={{
                    borderColor: !canUserEditTicket
                        ? '#d9d9d9'
                        : ticketCRs.length > 0
                            ? '#52c41a'
                            : '#667eea',
                    color: !canUserEditTicket
                        ? '#bfbfbf'
                        : ticketCRs.length > 0
                            ? '#52c41a'
                            : '#667eea',
                    fontWeight: 500,
                    cursor: canUserEditTicket ? 'pointer' : 'not-allowed'
                }}
                disabled={!canUserEditTicket}
            >
                Manage CR
            </Button>
        </Badge>
    )}

      {/* Log with CR Button - With CR Check */}
        {ticketData?.id && (
            <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={() => {
                    if (!canUserEditTicket) return;
                    
                    console.log('[Log with CR] Button clicked for ticket:', ticketData.id);
                    
                    // Show loading
                    message.loading({ content: 'Checking for existing CR/CM...', key: 'checkCR', duration: 0 });
                    
                    // Make the API call to check if ticket has CR
                    api.get(`/api/time-tracking/cr/ticket/${ticketData.id}`)
                        .then(response => {
                            console.log('[Log with CR] API Response:', response.data);
                            message.destroy('checkCR');
                            
                            if (response.data.success && response.data.data && response.data.data.length > 0) {
                                // Has CR → Navigate to CR Detail page of the first CR
                                const firstCR = response.data.data[0];
                                console.log('[Log with CR] Found CR, navigating to CR detail:', firstCR.id);
                                navigate(`/cr-management/${firstCR.id}`);
                            } else {
                                // No CR → Show notification with Create CR button
                                console.log('[Log with CR] No CR found');
                                
                                notificationApi.warning({
                                    key: 'noCRWarning',
                                    message: 'No CR/CM Found',
                                    description: (
                                        <div>
                                            <p>This ticket does not have any CR/CM yet.</p>
                                            <p style={{ marginTop: 8 }}>
                                                Please create a CR/CM first before logging time with CR.
                                            </p>
                                            <Button 
                                                type="primary" 
                                                size="small"
                                                onClick={() => {
                                                    notificationApi.destroy('noCRWarning');
                                                    navigate(`/cr-management/create?ticketId=${ticketData.id}`);
                                                }}
                                                style={{ marginTop: 8 }}
                                            >
                                                Create CR
                                            </Button>
                                        </div>
                                    ),
                                    duration: 10,
                                    placement: 'topRight',
                                });
                            }
                        })
                        .catch(error => {
                            message.destroy('checkCR');
                            console.error('[Log with CR] Error:', error);
                            console.error('[Log with CR] Error response:', error.response?.data);
                            message.error('Failed to check for existing CR/CM. Please try again.');
                        });
                }}
                style={{ 
                    borderColor: canUserEditTicket ? '#1890ff' : '#d9d9d9', 
                    color: canUserEditTicket ? '#1890ff' : '#bfbfbf',
                    fontWeight: 500,
                    cursor: canUserEditTicket ? 'pointer' : 'not-allowed'
                }}
                disabled={!canUserEditTicket}
            >
                Log with CR
            </Button>
      )}

      {/* NEW: Log Time without CR Button - Direct logging */}
      {ticketData?.id && (
          <Button
              type="default"
              icon={<ClockCircleOutlined />}
              onClick={() => {
                  if (!canUserEditTicket) return;
                  console.log('[Log Time No CR] Button clicked for ticket:', ticketData.id);
                  navigate(`/time-tracking/new?ticketId=${ticketData.id}&mode=direct`);
              }}
              style={{ 
                  borderColor: canUserEditTicket ? '#52c41a' : '#d9d9d9', 
                  color: canUserEditTicket ? '#52c41a' : '#bfbfbf',
                  fontWeight: 500,
                  cursor: canUserEditTicket ? 'pointer' : 'not-allowed'
              }}
              disabled={!canUserEditTicket}
          >
              Log Time (No CR)
          </Button>
      )}
      
      {/* Existing Report Buttons */}
      {canGenerateReport && (
        <>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => handleReportAction('preview')}
            loading={previewLoading}
            className="preview-report-button"
          >
            PREVIEW BORANG ADUAN
          </Button>
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => handleReportAction('download', key)}
                items={[
                  {
                    key: 'pdf',
                    label: 'Download as PDF',
                    icon: <DownloadOutlined />,
                  },
                  {
                    key: 'docx',
                    label: 'Download as Word',
                    icon: <DownloadOutlined />,
                  },
                ]}
              />
            }
            trigger={['click']}
            disabled={isTicketClosed && !canUserEditTicket}
          >
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadLoading}
              className="download-report-button"
            >
              DOWNLOAD BORANG ADUAN
            </Button>
          </Dropdown>
        </>
      )}
    </div>
  </div>

        <Text type="secondary" className="edit-ticket-description-text">
          Make changes to the ticket details below.
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={handleFormSubmissionFailed}
          className="edit-ticket-form"
        >
          <Tabs defaultActiveKey="main" className="ticket-tabs">
            <Tabs.TabPane tab="Main Details" key="main">
                <Row gutter={[24, 16]}>
                    {permissions.canEdit('tickets', PERMISSION_LEVELS.ASSIGNED) && (
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="currentStatusId"
                                label={<Text strong>Ticket Status</Text>}
                                rules={[
                                    { required: true, message: 'Please select a status!' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value) {
                                                return Promise.reject(new Error('Please select a status!'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                                className="form-item-spacing"
                            >
                                <Select
                                    placeholder="Select status"
                                    className="custom-select custom-input-height custom-input-border-radius"
                                    allowClear={false}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children && option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    disabled={!canUserEditTicket}
                                >
                                    {Array.isArray(ticketStatuses) && ticketStatuses.map(status => (
                                        <Option 
                                            key={status.id} 
                                            value={status.id}
                                            disabled={isTicketCurrentlyUnassigned && status.id !== STATUS_IN_PROGRESS_ID && status.id !== STATUS_CANCELLED_ID}
                                        >
                                            {status.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    <Col xs={24} md={12}>
                    <Form.Item
                    name="assignedToUserId"
                    label={<Text strong>Assignee</Text>}
                    className="form-item-spacing"
                    >
                    <Select
                        placeholder="Select an assignee (Optional)"
                        className="custom-select custom-input-height custom-input-border-radius"
                        allowClear={true}
                        showSearch
                        filterOption={(input, option) => {
                        if (!option?.children) return false;
                        const text = Array.isArray(option.children) 
                            ? option.children.join(' ')
                            : option.children.toString();
                        return text.toLowerCase().includes(input.toLowerCase());
                        }}
                        disabled={isAssigneeDisabled || !canUserEditTicket}
                        optionLabelProp="label"
                    >
                        <Option key="unassigned" value={null} label="Unassigned">
                        Unassigned
                        </Option>
                        {Array.isArray(assignees) && assignees.map(assignee => (
                        <Option 
                            key={assignee.id} 
                            value={assignee.id}
                            label={`${assignee.firstName} ${assignee.lastName}`}
                        >
                            {assignee.firstName} {assignee.lastName}
                            {assignee.email && ` (${assignee.email})`}
                        </Option>
                        ))}
                    </Select>
                    </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="contractId"
                            label={<Text strong>Contract</Text>}
                            rules={[
                                { required: true, message: 'Please select a contract!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select a contract!'));
                                        }
                                        return Promise.resolve();
                                        },
                                    }),
                                ]}
                                className="form-item-spacing"
                            >
                                <Select
                                    placeholder="Select a contract"
                                    className="custom-select custom-input-height custom-input-border-radius"
                                    allowClear={false}
                                    showSearch
                                    onChange={handleContractChange}
                                    filterOption={(input, option) =>
                                        option.children && option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    disabled={!canUserEditTicket}
                                >
                                    {Array.isArray(contracts) && contracts.map(contract => (
                                        <Option key={contract.id} value={contract.id}>
                                            {contract.contractName || `Contract #${contract.id}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="ticketCategoryId"
                            label={<Text strong>Ticket Category</Text>}
                            rules={[
                                { required: true, message: 'Please select a ticket category!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select a ticket category!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            className="form-item-spacing"
                        >
                            <Select
                                placeholder="Select a category"
                                className="custom-select custom-input-height custom-input-border-radius"
                                allowClear={false}
                                showSearch
                                onChange={handleTicketCategoryChange}
                                filterOption={(input, option) =>
                                    option.children && option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                disabled={!canUserEditTicket}
                            >
                                {contractSpecificCategories.length > 0
                                  ? contractSpecificCategories.map(category => (
                                      <Option key={category.id} value={category.id}>
                                          {category.name}
                                      </Option>
                                    ))
                                  : Array.isArray(ticketCategories) && ticketCategories.map(category => (
                                      <Option key={category.id} value={category.id}>
                                          {category.name}
                                      </Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="productCategoryId"
                            label={<Text strong>Product Category</Text>}
                            rules={[
                                { required: true, message: 'Please select a product category!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select a product category!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            className="form-item-spacing"
                        >
                            <Select
                                placeholder="Select product category"
                                className="custom-select custom-input-height custom-input-border-radius"
                                onChange={handleProductCategoryChange}
                                allowClear={false}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children && typeof option.children === 'string' &&
                                    (option.children.toLowerCase().includes(input.toLowerCase()) ||
                                     (option.value && productCategories.find(pc => pc.id === option.value && pc.code && pc.code.toLowerCase().includes(input.toLowerCase()))))
                                }
                                disabled={!canUserEditTicket}
                            >
                                {Array.isArray(productCategories) && productCategories.map(pCat => (
                                    <Option key={pCat.id} value={pCat.id}>
                                        {pCat.name} {pCat.code ? `| ${pCat.code}` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="productId"
                            label={<Text strong>Product</Text>}
                            rules={[{
                                required: true,
                                message: 'Please select a product!',
                                validator: (_, value) => {
                                    if (filteredProducts.length > 0 && !value) {
                                        return Promise.reject(new Error('Please select a product!'));
                                    }
                                    return Promise.resolve();
                                }
                            }]}
                            className="form-item-spacing"
                        >
                            <Select
                                key={`product-select-${selectedContractId}`}
                                placeholder={filteredProducts.length === 0 ? "No products available for selected category" : "Select product"}
                                className="custom-select custom-input-height custom-input-border-radius"
                                allowClear={false}
                                showSearch
                                filterOption={(input, option) => {
                                    if (!option?.children) return false;
                                    const text = Array.isArray(option.children) 
                                        ? option.children.join(' ')
                                        : option.children.toString();
                                    return text.toLowerCase().includes(input.toLowerCase());
                                }}
                                disabled={!canUserEditTicket || !selectedProductCategoryId || filteredProducts.length === 0}
                                optionLabelProp="label"
                            >
                                {/* Always show current product (for viewing) */}
                                {ticketData?.productId && (
                                    <Option 
                                        key={ticketData.productId} 
                                        value={ticketData.productId}
                                        label={ticketData.productName || `Product #${ticketData.productId}`}
                                    >
                                        {ticketData.productName || `Product #${ticketData.productId}`}
                                        {ticketData.productCode ? ` | ${ticketData.productCode}` : ''} (Current)
                                    </Option>
                                )}
                                {/* Only show OTHER products if user can edit */}
                                {canUserEditTicket && Array.isArray(filteredProducts) && filteredProducts
                                    .filter(product => !ticketData?.productId || String(product.id) !== String(ticketData.productId))
                                    .map(product => (
                                        <Option 
                                            key={product.id} 
                                            value={product.id}
                                            label={product.name || `Product #${product.id}`}
                                        >
                                            {product.name || `Product #${product.id}`} {product.code ? `| ${product.code}` : ''}
                                        </Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="title"
                    label={<Text strong>Ticket Title</Text>}
                    rules={[
                        { required: true, message: 'Please enter a ticket title!' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || value.trim() === '') {
                                    return Promise.reject(new Error('Please enter a ticket title!'));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                    className="form-item-spacing"
                >
                    <Input
                        placeholder="Brief summary of the issue"
                        className="ant-input-lg custom-input-height custom-input-border-radius"
                        disabled={!canUserEditTicket}
                    />
                </Form.Item>

                <Form.Item
                    label={<Text strong>Description</Text>}
                    className="form-item-spacing"
                    rules={[{
                        validator: async () => {
                            const cleanedDescription = descriptionHtml.replace(/<[^>]*>?/gm, '').trim();
                            if (cleanedDescription === '') {
                                return Promise.reject(new Error('Please enter a detailed description!'));
                            }
                            return Promise.resolve();
                        },
                    }]}
                >
                    <Editor
                      tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                      onInit={(evt, editor) => editorRef.current = editor}
                      value={descriptionHtml}
                      onEditorChange={(newValue, editor) => setDescriptionHtml(newValue)}
                      init={{
                        license_key: 'gpl',
                        base_url: '/tinymce',
                        suffix: '.min',
                        height: 300,
                        menubar: false,
                        plugins: [
                          'lists', 'code', 'advlist', 'autolink', 'link', 'image',
                          'charmap', 'wordcount', 'searchreplace', 'visualblocks',
                          'fullscreen', 'insertdatetime', 'table', 'media', 'help'
                        ],
                        toolbar: 'undo redo | formatselect | bold italic backcolor | ' +
                                 'alignleft aligncenter alignright alignjustify | ' +
                                 'bullist numlist outdent indent | link image charmap | ' +
                                 'searchreplace visualblocks fullscreen | insertdatetime media table | code wordcount help | removeformat',
                        content_style: 'body { font-family:Inter,sans-serif; font-size:14px }',
                      }}
                      disabled={!canUserEditTicket}
                    />
                    <Button
                        type="default"
                        icon={<ClearOutlined />}
                        onClick={handleClearDescription}
                        style={{ marginTop: '10px', float: 'right' }}
                        disabled={!canUserEditTicket}
                    >
                        Clear Description
                    </Button>
                </Form.Item>

                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="caseReportedDate"
                            label={<Text strong>Case Reported Date & Time</Text>}
                            rules={[
                                { required: true, message: 'Please select the reported date and time!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select the reported date and time!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            className="form-item-spacing"
                        >
                            <DatePicker
                                showTime={{ format: 'hh:mm A', use12Hours: true }}
                                format={displayDateFormat}
                                className="ant-input-lg custom-input-height custom-input-border-radius custom-date-picker-width"
                                placeholder="Select Date & Time"
                                disabled={!canUserEditTicket}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="priorityId"
                            label={<Text strong>Priority</Text>}
                            rules={[
                                { required: true, message: 'Please select a priority!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select a priority!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            className="form-item-spacing"
                        >
                            <Select
                                placeholder="Select priority"
                                className="custom-select custom-input-height custom-input-border-radius"
                                allowClear={false}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children && option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                disabled={!canUserEditTicket}
                            >
                                {Array.isArray(displayedPriorities) && displayedPriorities.map(priority => (
                                    <Option key={priority.id} value={priority.id}>
                                        {priority.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                
                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="ticketTypeId"
                            label={<Text strong>Ticket Type</Text>}
                            rules={[
                                { required: true, message: 'Please select a ticket type!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.reject(new Error('Please select a ticket type!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            className="form-item-spacing"
                        >
                            <Select
                                placeholder="Select type"
                                className="custom-select custom-input-height custom-input-border-radius"
                                allowClear={false}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children && option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                disabled={!canUserEditTicket}
                            >
                                {Array.isArray(ticketTypes) && ticketTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        {type.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="dueDate"
                            label={<Text strong>Due Date & Time (Optional)</Text>}
                            className="form-item-spacing"
                        >
                            <DatePicker
                                showTime={{ format: 'hh:mm A', use12Hours: true }}
                                format={displayDateFormat}
                                className="ant-input-lg custom-input-height custom-input-border-radius custom-date-picker-width"
                                placeholder="Select Due Date & Time"
                                disabled={!canUserEditTicket}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="agreedResolutionPeriodValue"
                            label={<Text strong>Agreed Resolution Period (Hours, Optional)</Text>}
                            className="form-item-spacing"
                        >
                            <Input
                                type="number"
                                min={0}
                                placeholder="e.g., 24"
                                className="ant-input-lg custom-input-height custom-input-border-radius"
                                disabled={!canUserEditTicket}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '20px' }}>
                        <Form.Item
                            name="agreedToExtendTime"
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox disabled={!canUserEditTicket}>
                                <Text strong>Agreed to Extend Time?</Text>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

            </Tabs.TabPane>

            <Tabs.TabPane tab="Attachments" key="attachments">
              <Title level={4}>Existing Attachments</Title>
              {existingAttachments.length === 0 ? (
                <Text type="secondary">No attachments for this ticket yet.</Text>
              ) : (
                  <div className="existing-attachments-list">
                    {existingAttachments.map(attachment => (
                      <div key={attachment.id} className="attachment-item">
                      <div className="attachment-content">
                        <a href={attachment.filePath} target="_blank" rel="noopener noreferrer">
                          {attachment.fileName} 
                          {attachment.fileSizeBytes && attachment.fileSizeBytes > 0 ? 
                            ` (${Math.round(attachment.fileSizeBytes / 1024)} KB)` : 
                            ' (Size unknown)'}
                        </a>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                            Added by: {
                              // Try multiple possible field names for user information
                              attachment.userFirstName || 
                              attachment.firstName || 
                              attachment.first_name ||
                              'Unknown'
                            }{' '}
                            {
                              attachment.userLastName || 
                              attachment.lastName || 
                              attachment.last_name || 
                              ''
                            }
                          </Text>
                      </div>
                      {canUserEditTicket && (
                      <Button
                        type="text"
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleDeleteExistingAttachment(attachment.id)}
                        size="small"
                        className="delete-attachment-btn"
                      />
                      )}
                    </div>
                  ))}
                </div>
              )}
                <Title level={4} style={{ marginTop: '20px' }}>Add New Attachments</Title>
                <Form.Item className="form-item-spacing">
                <Upload
                  name="attachments"
                  multiple={true}
                  fileList={fileList}
                  onChange={handleFileChange}
                  onRemove={(file) => {
                    return true;
                  }}
                  beforeUpload={() => false}
                  customRequest={dummyRequest}
                  showUploadList={{
                    showRemoveIcon: true,
                    removeIcon: <CloseCircleOutlined style={{ color: 'red' }} />,
                  }}
                  listType="text"
                  disabled={!canUserEditTicket}
                >
                        <Button icon={<UploadOutlined />} className="create-ticket-upload-button" disabled={!canUserEditTicket}>
                            Select File(s)
                        </Button>
                    </Upload>
                    <Text type="secondary" className="upload-hint">
                        You can upload new files (max 5MB per file). Existing attachments are listed above.
                    </Text>
                </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Comments" key="comments">
              <div className="comments-section" style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '15px' }}>
                {comments.length === 0 ? (
                  <Text type="secondary">No comments or activity for this ticket yet.</Text>
                ) : (
                  comments.map(comment => renderComment(comment))
                )}
              </div>
              
              <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                <Title level={4} style={{ marginBottom: '10px' }}>Add a New Comment</Title>
                <TextArea
                  rows={4}
                  value={newGeneralCommentText}
                  onChange={(e) => setNewGeneralCommentText(e.target.value)}
                  placeholder="Enter your comment here..."
                  style={{ marginBottom: '10px', borderRadius: '8px' }}
                  disabled={!canUserEditTicket}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAddGeneralComment}
                  loading={loading}
                  style={{ float: 'right', borderRadius: '8px' }}
                  disabled={!canUserEditTicket}
                >
                  Add Comment
                </Button>
                <div style={{ clear: 'both' }}></div>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Involved Assets" key="involvedAssets">
            {ticketId && (
                <TicketInvolvedAssetsManager
                ticketId={parseInt(ticketId)}
                user={user}
                permissions={permissions}
                notificationApi={notificationApi}
                assignees={assignees}
                ticketData={{
                    ...ticketData,
                    createdByUserId: ticketData.createdByUserId || ticketData.created_by_user_id,
                    organizationId: ticketData.organizationId || ticketData.organization_id,
                    assignedToUserId: ticketData.assignedToUserId || ticketData.assigned_to_user_id
                }}
                canEdit={canUserEditTicket}
                />
            )}
            </Tabs.TabPane>

            {/* Always show Internal Notes and Resolution Details tabs for users with appropriate permissions */}
            {permissions.canEdit('tickets', PERMISSION_LEVELS.ASSIGNED) && (
                <>
                    <Tabs.TabPane tab="Internal Notes" key="internalNotes">
                        <Form.Item
                            name="internalDescription"
                            label={<Text strong>Internal Description</Text>}
                            className="form-item-spacing"
                        >
                            <TextArea 
                                rows={4} 
                                placeholder="Internal notes for support staff" 
                                className="ant-input-lg custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Resolution Details" key="resolutionDetails">
                        <Form.Item
                            name="resolutionTaken"
                            label={<Text strong>Resolution Taken</Text>}
                            className="form-item-spacing"
                        >
                            <TextArea 
                                rows={4} 
                                placeholder="Details of the resolution provided" 
                                className="ant-input-lg custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
                        <Form.Item
                            name="responseType"
                            label={<Text strong>Response Type</Text>}
                            className="form-item-spacing"
                        >
                            <Input 
                                placeholder="e.g., Email, Phone Call, On-site" 
                                className="ant-input-lg custom-input-height custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
                        <Form.Item
                            name="cause"
                            label={<Text strong>Cause</Text>}
                            className="form-item-spacing"
                        >
                            <Input 
                                placeholder="Root cause of the issue" 
                                className="ant-input-lg custom-input-height custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
                        <Form.Item
                            name="impact"
                            label={<Text strong>Impact</Text>}
                            className="form-item-spacing"
                        >
                            <Input 
                                placeholder="Impact of the issue" 
                                className="ant-input-lg custom-input-height custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
                        <Form.Item
                            name="remedialActions"
                            label={<Text strong>Remedial Actions</Text>}
                            className="form-item-spacing"
                        >
                            <TextArea 
                                rows={4} 
                                placeholder="Actions taken to prevent recurrence" 
                                className="ant-input-lg custom-input-border-radius" 
                                disabled={!canUserEditTicket} 
                            />
                        </Form.Item>
<Form.Item
  name="proposedResolveDate"
  label={<Text strong>Proposed Resolve Date & Time</Text>}
  className="form-item-spacing"
>
  <DatePicker
    showTime={{ format: 'hh:mm A', use12Hours: true }}
    format="YYYY-MM-DD hh:mm A"
    className="ant-input-lg custom-input-height custom-input-border-radius custom-date-picker-width"
    placeholder="Select Proposed Resolve Date & Time"
    disabled={!canUserEditTicket}
    onChange={(value) => {
      console.log('proposedResolveDate changed to:', value?.format('YYYY-MM-DD hh:mm A')); // Debug log
      const newValue = value || (ticketData?.proposedResolveDate ? dayjs(ticketData.proposedResolveDate) : dayjs().tz('Asia/Kuala_Lumpur'));
      form.setFieldsValue({ proposedResolveDate: newValue });
    }}
  />
</Form.Item>
<Form.Item
  name="proposedEndDate"
  label={<Text strong>Proposed End Date & Time</Text>}
  className="form-item-spacing"
>
  <DatePicker
    showTime={{ format: 'hh:mm A', use12Hours: true }}
    format="YYYY-MM-DD hh:mm A"
    className="ant-input-lg custom-input-height custom-input-border-radius custom-date-picker-width"
    placeholder="Select Proposed End Date & Time"
    disabled={!canUserEditTicket}
    onChange={(value) => {
      console.log('proposedEndDate changed to:', value?.format('YYYY-MM-DD hh:mm A')); // Debug log
      const newValue = value || (ticketData?.proposedEndDate ? dayjs(ticketData.proposedEndDate) : dayjs().tz('Asia/Kuala_Lumpur'));
      form.setFieldsValue({ proposedEndDate: newValue });
    }}
  />
</Form.Item>
                    </Tabs.TabPane>
                </>
            )}
          </Tabs>

          <div style={{ marginTop: '20px', textAlign: 'left' }}>
            <Text type="secondary" style={{ marginRight: '10px' }}>
                Changes made on the Main Details, Internal Notes, Resolution Details, and Attachments tabs will be saved when you click 'Update Ticket'. Comments and replies are saved instantly.
            </Text>
            {canUserEditTicket && (
              <Form.Item className="submit-button-item" style={{ display: 'inline-block', marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  className="edit-ticket-submit-button"
                >
                  Update Ticket
                </Button>
              </Form.Item>
            )}
          </div>
        </Form>

        <PdfPreviewModal 
          visible={isPreviewModalVisible}
          onClose={handlePreviewModalClose}
          pdfData={pdfPreviewBuffer}
          title={pdfPreviewTitle}
        />

        <Modal
          title="Add Comment for Status Change"
          open={isCommentModalVisible}
          onOk={handleCommentModalOk}
          onCancel={handleCommentModalCancel}
          confirmLoading={loading}
          maskClosable={false}
          destroyOnClose={true}
        >
          <Form layout="vertical">
            <Form.Item
              label={<><Text strong>Comment</Text><Text type="secondary"> (Required for status changes)</Text></>}
              required
            >
              <TextArea
                rows={4}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add comment regarding this status change..."
              />
            </Form.Item>
          </Form>
        </Modal>

      {/* NEW: Date Update Modal */}
            <Modal
              title="Update Dates for Closed Ticket"
              open={isDateModalVisible}
              onOk={handleDateModalOk}
              onCancel={handleDateModalCancel}
              confirmLoading={loading}
              maskClosable={false}
              destroyOnClose={true}
            >
              <Form form={dateForm} layout="vertical">
                <Form.Item
                  name="caseReportedDate"
                  label="Case Reported Date"
                  rules={[{ required: true, message: 'Please select the case reported date!' }]}
                >
                  <Row gutter={8}>
                    <Col span={20}>
                      <DatePicker
                        key={`caseReportedDate-${datePickerKey}`}
                        showTime={{ format: 'hh:mm A' }}
                        format="YYYY-MM-DD hh:mm A"
                        value={caseReportedDate}
                        onChange={(value) => {
                          console.log('caseReportedDate changed to:', value?.format('YYYY-MM-DD hh:mm A'));
                          const newValue = value || dayjs().tz('Asia/Kuala_Lumpur');
                          setCaseReportedDate(newValue);
                          dateForm.setFieldsValue({ caseReportedDate: newValue });
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select case reported date"
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        onClick={() => {
                          const now = dayjs().tz('Asia/Kuala_Lumpur');
                          console.log('Setting caseReportedDate to:', now.format('YYYY-MM-DD hh:mm A'));
                          setCaseReportedDate(now);
                          dateForm.setFieldsValue({ caseReportedDate: now });
                          setDatePickerKey(prev => prev + 1);
                        }}
                      >
                        Now
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
                <Form.Item
                  name="closedAt"
                  label="Closed At Date"
                  rules={[{ required: true, message: 'Please select the closed at date!' }]}
                >
                  <Row gutter={8}>
                    <Col span={20}>
                      <DatePicker
                        key={`closedAt-${datePickerKey}`}
                        showTime={{ format: 'hh:mm A' }}
                        format="YYYY-MM-DD hh:mm A"
                        value={closedAt}
                        onChange={(value) => {
                          console.log('closedAt changed to:', value?.format('YYYY-MM-DD hh:mm A'));
                          const newValue = value || dayjs().tz('Asia/Kuala_Lumpur');
                          setClosedAt(newValue);
                          dateForm.setFieldsValue({ closedAt: newValue });
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select closed at date"
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        onClick={() => {
                          const now = dayjs().tz('Asia/Kuala_Lumpur');
                          console.log('Setting closedAt to:', now.format('YYYY-MM-DD hh:mm A'));
                          setClosedAt(now);
                          dateForm.setFieldsValue({ closedAt: now });
                          setDatePickerKey(prev => prev + 1);
                        }}
                      >
                        Now
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
              </Form>
            </Modal>

        <Modal
          title="Edit Comment"
          open={isEditCommentModalVisible}
          onOk={handleUpdateComment}
          onCancel={handleCancelEditComment}
          confirmLoading={loading}
          maskClosable={false}
          destroyOnClose={true}
        >
          <Form layout="vertical">
            <Form.Item
              label={<Text strong>Edit Comment</Text>}
              required
            >
              <TextArea
                rows={4}
                value={editingCommentText}
                onChange={(e) => setEditingCommentText(e.target.value)}
                placeholder="Edit your comment..."
              />
            </Form.Item>
          </Form>
        </Modal>

<Modal
  title="Confirm Comment Deletion"
  open={deleteCommentModalVisible}
  onOk={handleConfirmDeleteComment}
  onCancel={() => {
    setDeleteCommentModalVisible(false);
    setCommentToDelete(null);
  }}
  okText="Yes, Delete"
  okType="danger"
  cancelText="Cancel"
  confirmLoading={loading}
>
  <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
</Modal>

        <Modal
          title="Confirm Deletion"
          open={deleteModalVisible}
          onOk={handleConfirmDelete}
          onCancel={handleCancelDelete}
          okText="Yes"
          okType="danger"
          cancelText="No"
        >
          <p>Are you sure you want to delete this attachment?</p>
        </Modal>
      </div>
    </Card>
  );
};

export default EditTicketForm;